/* eslint-disable */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ECharts from "echarts-for-react";
import dayjs from "utils/dayjs";
// import "dayjs/locale/ko";
// import "dayjs/locale/zh-cn";

import MainCard from "components/MainCard";
import useConfig from "hooks/useConfig";
import { getEquipmentMeasurementLogsStatsDurationQueryString } from "components/axios/equipmentAxios";

const DELAY_TIME = Number(process.env.REACT_APP_DELAY_TIME) || 60000;
const DEVICE_TIMEZONE = process.env.REACT_APP_TIMEZONE || "Asia/Seoul";

/* =========================
   ST ON/OFF 판별
========================= */
function isSTOn(d) {
    // if (d.equipmentPartCode === "DVM" || d.equipmentPartCode === "DVM2") {
    //     return d.value === "1";
    // }
    // return d.value === "0";
    return d.value === "1";
}

/* =========================
   timestamp 생성 (정렬용)
========================= */
function toTimestamp(d, paramType) {
    // 년, 월, 일을 문자열 포맷으로 변환 (padStart로 2자리 유지)
    const dateStr = `${d.year}-${String(d.month).padStart(2, "0")}-${String(d.day).padStart(2, "0")}`;
    
    let fullStr = "";
    if (paramType === "DAY") {
        fullStr = `${dateStr} ${String(d.hour).padStart(2, "0")}:00:00`;
    } else {
        fullStr = `${dateStr} 00:00:00`;
    }

    // 브라우저 시간이 아닌 DEVICE_TIMEZONE 기준으로 타임스탬프 해석
    return dayjs.tz(fullStr, DEVICE_TIMEZONE).valueOf();
}

/* =========================
   x축 라벨 포맷
========================= */
function formatXAxisLabel(ts, paramType, t) {
    // 출력 시에도 장비 타임존 기준으로 변환하여 포맷팅
    const d = dayjs(ts).tz(DEVICE_TIMEZONE);

    if (paramType === "DAY") {
        return d.format(t("message.common.text.date.format.day"));
    }
    if (paramType === "MONTH") {
        return d.format(t("message.common.text.date.format.month"));
    }
    return d.format(t("message.common.text.date.format.year"));
}

/* =========================
   ON 구간 병합 (Gantt 데이터)
========================= */
function buildGanttData(raw, paramType) {
    const sorted = [...raw].sort(
        (a, b) => toTimestamp(a, paramType) - toTimestamp(b, paramType)
    );

    const result = [];
    let current = null;

    sorted.forEach(d => {
        const ts = toTimestamp(d, paramType);
        const isOn = isSTOn(d);

        if (isOn) {
            if (!current) {
                current = { start: ts, end: ts };
            } else {
                current.end = ts;
            }
        } else {
            if (current) {
                result.push({ ...current });
                current = null;
            }
        }
    });

    if (current) result.push(current);

    return result;
}

/* =========================
   메인 컴포넌트
========================= */
function EquipmentPartMeasurementDurationGraphs({ equipmentPartId, equipmentPartName, componentLocationId }) {
    const { t } = useTranslation();
    const { mode } = useConfig();

    const textColor = mode === "light" ? "#262626" : "#ffffff";
    const barColor = "#2672f7";
    // mode === "light" ? "#f9f9fa" : "#2f2f2f";

    const paramType = "DAY";

    const [data, setData] = useState([]);
    const prevHashRef = useRef("");

    /* =========================
       API 호출
    ========================= */
    const fetchData = async () => {
        const query = `?componentLocation.id=${componentLocationId}` +
            `&equipmentPart.id=${equipmentPartId}` +
            `&code=ST`;
        const res = await getEquipmentMeasurementLogsStatsDurationQueryString(
            paramType,
            query
        );
            
        return res || [];
    };

    /* =========================
       Polling (변경 시만 업데이트)
    ========================= */
    useEffect(() => {
        let mounted = true;

        const load = async () => {
            const res = await fetchData();
            const hash = JSON.stringify(res);

            if (mounted && hash !== prevHashRef.current) {
                prevHashRef.current = hash;
                setData(res);
            }
        };

        load();
        const timer = setInterval(load, DELAY_TIME);

        return () => {
            mounted = false;
            clearInterval(timer);
        };
    }, [equipmentPartId, componentLocationId, paramType]);

    /* =========================
       Gantt 데이터
    ========================= */
    const ganttData = useMemo(
        // () => buildGanttData(data.filter(d => d.code === "ST"), paramType),
        () => buildGanttData(data, paramType),
        [data, paramType]
    );

    /* =========================
        xAxis 범위 (최근 24시간)
    ========================= */
    // const now = Date.now();
    // const from24h = now - 24 * 60 * 60 * 1000;
    // const nowInDevice = useMemo(() => dayjs().tz(DEVICE_TIMEZONE), []);
    // const nowTs = nowInDevice.valueOf();
    // const from24hTs = nowInDevice.subtract(24, "hour").valueOf();
    const nowTs = dayjs().tz(DEVICE_TIMEZONE).valueOf();
    const from24hTs = dayjs()
        .tz(DEVICE_TIMEZONE)
        .subtract(24, "hour")
        .valueOf();

    /* =========================
       ECharts 옵션
    ========================= */
    const option = useMemo(() => ({
        tooltip: {
            trigger: "item",
        },
        grid: {
            left: 10,
            right: 20,
            top: 20,
            bottom: 40,
        },
        xAxis: {
            type: "time",
            min: paramType === "DAY" ? from24hTs : (ganttData[0]?.start || from24hTs),
            max: paramType === "DAY" ? nowTs : (ganttData[ganttData.length - 1]?.end || nowTs),
            axisLabel: {
                color: textColor,
                formatter: v => formatXAxisLabel(v, paramType, t),
            },
        },
        yAxis: {
            type: "category",
            data: ["ST"],
            show: false,
        },
        series: [
        {
            type: "custom",
            renderItem: (params, api) => {
                const y = 0;
                const start = api.coord([api.value(0), y]);
                const end = api.coord([api.value(1), y]);
                const height = api.size([0, 1])[1] * 0.6;

                return {
                    type: "rect",
                    shape: {
                        x: start[0],
                        y: start[1] - height / 2,
                        width: end[0] - start[0],
                        height,
                    },
                    style: {
                        fill: barColor,
                    },
                };
            },
            encode: {
                x: [0, 1],
                y: 0,
            },
            tooltip: {
                formatter: params => {
                    const [start, end] = params.value;
                    const durMin = Math.round((end - start) / 60000);
                    const dateTimeFmt = t("message.common.text.date.format.datetime");

                    return `
                    ${t('message.common.text.start')}: ${dayjs(start).tz(DEVICE_TIMEZONE).format(dateTimeFmt)}<br/>
                    ${t('message.common.text.end')}:  ${dayjs(end).tz(DEVICE_TIMEZONE).format(dateTimeFmt)}<br/>
                    ${t('message.common.text.duration')}: ${durMin} ${t('message.common.text.minute')}
                    `;
                },
            },
            data: ganttData.map(d => [d.start, d.end]),
        },
        ],
    }), [ganttData, t, from24hTs, nowTs, textColor]);

    if (!ganttData.length) return null;

    return (
        <React.Fragment>
            <MainCard content={false}>
                <div style={{textAlign:"center", marginBottom: 4}}>
                    {t("message.common.chart.onOff", { name: equipmentPartName })}
                </div>
                <ECharts
                    option={option}
                    style={{ height: 150, width: "100%" }}
                />
                </MainCard>
            <div className="divide" />
        </React.Fragment>
    );
}

export default EquipmentPartMeasurementDurationGraphs;
